import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { errorMessage } from "@/api/errors";
import { useSavedIds, useToggleWishlist } from "@/features/wishlist/api";
import { cn } from "@/lib/cn";
import { prefersReducedMotion } from "@/motion/useReducedMotion";
import { useAuth } from "@/stores/auth";
import { toast } from "@/stores/toast";

const COLORS = ["#EC4899", "#7C5CFF", "#22D3EE", "#F5F7FF"];

function burst(host: HTMLElement) {
  if (prefersReducedMotion()) return;
  for (let i = 0; i < 10; i++) {
    const dot = document.createElement("span");
    dot.setAttribute("aria-hidden", "true");
    Object.assign(dot.style, {
      position: "absolute",
      left: "50%",
      top: "50%",
      width: "6px",
      height: "6px",
      borderRadius: "999px",
      background: COLORS[i % COLORS.length],
      pointerEvents: "none",
    });
    host.appendChild(dot);
    const angle = (Math.PI * 2 * i) / 10;
    if (typeof dot.animate !== "function") {
      dot.remove();
      continue;
    }
    dot
      .animate(
        [
          { transform: "translate(-3px,-3px) scale(1)", opacity: 1 },
          { transform: `translate(${Math.cos(angle) * 26 - 3}px,${Math.sin(angle) * 26 - 3}px) scale(0.2)`, opacity: 0 },
        ],
        { duration: 600, easing: "cubic-bezier(.2,.8,.2,1)" },
      )
      .finished.then(() => dot.remove())
      .catch(() => dot.remove());
  }
}

/** Save/unsave a listing. Signed-out users are sent to log in first. */
export function WishlistHeart({
  productId,
  slug,
  title,
  className,
  size = "md",
}: {
  productId: string;
  slug: string;
  title: string;
  className?: string;
  size?: "sm" | "md";
}) {
  const authed = useAuth((s) => s.status === "authenticated");
  const saved = useSavedIds().data?.has(productId) ?? false;
  const toggle = useToggleWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={saved}
      aria-label={saved ? `Remove ${title} from saved items` : `Save ${title}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!authed) {
          navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
          return;
        }
        if (!saved && ref.current) burst(ref.current);
        toggle.mutate({ slug, productId }, { onError: (err) => toast.error(errorMessage(err)) });
      }}
      className={cn(
        "relative grid place-items-center rounded-full border backdrop-blur transition",
        size === "sm" ? "size-9" : "size-11",
        saved
          ? "border-magenta/60 bg-magenta/15 text-magenta shadow-[0_0_18px_rgb(236_72_153/0.45)]"
          : "border-fg/15 bg-void/60 text-ink-soft hover:text-ink",
        className,
      )}
    >
      <Heart className={cn(size === "sm" ? "size-4" : "size-5", saved && "fill-current")} aria-hidden />
    </button>
  );
}
